/* eslint-disable */
// /src/pages/AuthPhonePage.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { getSession } from "../services/authService";
import { genOtp, toE164KR, inTestRange, sendOtp } from "../services/smsService";

export default function AuthPhonePage() {
    const nav = useNavigate();

    // ✅ 세션 감지 → 이미 로그인 상태면 /home 으로 이동
    useEffect(() => {
        const s = getSession();
        if (s && s.token) nav("/home", { replace: true });
    }, [nav]);

    const [digits, setDigits] = useState("");
    const [code, setCode] = useState("");
    const [devCode, setDevCode] = useState("");
    const [sentTo, setSentTo] = useState("");
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [sent, setSent] = useState(false);

    const codeRef = useRef(null);
    const canSend = digits.replace(/\D/g, "").length >= 10 && secondsLeft === 0;

    const handleBack = () => {
        nav(-1);
    };

    // ✅ 1초 타이머 (쿨다운)
    useEffect(() => {
        if (secondsLeft <= 0) return;
        const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, [secondsLeft]);

    // ✅ 인증코드 발송
    const onSend = async () => {
        if (!canSend || sending) return;
        try {
            setSending(true);
            setError("");
            const otp = genOtp();
            const e164 = toE164KR(digits);
            const isDev = inTestRange(digits);

            setDevCode(otp);
            setSentTo(e164);
            setSecondsLeft(60);
            setTimeout(() => codeRef.current?.focus(), 50);
            setSent(true);

            if (isDev) return; // 테스트 구간은 서버 호출 생략

            const ok = await sendOtp(digits, otp);
            if (!ok) setError("인증 코드를 전송하지 못했습니다. 잠시 후 다시 시도해주세요.");
        } catch {
            setError("코드 전송 중 문제가 발생했습니다. 잠시 후 다시 시도해주세요.");
        } finally {
            setSending(false);
        }
    };

    // ✅ 코드 검증
    const onVerify = (e) => {
        e.preventDefault();
        setError("");
        const isDev = inTestRange(digits);
        if (!sent) {
            setError("먼저 인증 코드를 전송해주세요.");
            return;
        }
        if (!code || code.length < 4) {
            setError("인증 코드를 입력해주세요.");
            return;
        }

        // 운영환경: 서버 검증 없음 → 클라에서만 일치 검사
        const expected = devCode;
        const ok = isDev ? code === expected : code === expected;
        if (!ok) {
            setError("인증 코드가 올바르지 않습니다.");
            return;
        }

        // ✅ 성공 → 다음 단계로 이동
        nav("/auth/signup", { replace: true, state: { phoneE164: sentTo, verified: true } });
    };

    return (
        <Wrap>
            <Card>
                <HeaderRow>
                    <BackButton type="button" onClick={handleBack}>
                        <span className="arrow">←</span>
                        <span className="label">뒤로</span>
                    </BackButton>
                </HeaderRow>

                <Title>휴대폰 인증</Title>

                <Field>
                    <label>휴대폰 번호</label>
                    <input
                        value={digits}
                        onChange={(e) => setDigits(e.target.value)}
                        placeholder="010-1234-5678"
                        inputMode="numeric"
                    />
                    <PhoneHint>
                        카카오T 가맹기사용 앱에 등록된 휴대폰 번호로 가입해 주세요.
                    </PhoneHint>
                </Field>

                <SendRow>
                    <button type="button" disabled={!canSend || sending} onClick={onSend}>
                        {sending
                            ? "전송 중..."
                            : secondsLeft > 0
                                ? `재전송 (${secondsLeft}s)`
                                : "인증코드 전송"}
                    </button>
                </SendRow>

                <form onSubmit={onVerify}>
                    <Field>
                        <label>인증 코드</label>
                        <input
                            ref={codeRef}
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="6자리 코드"
                            inputMode="numeric"
                        />
                    </Field>

                    {error && <ErrorMsg>{error}</ErrorMsg>}

                    <NextBtn type="submit">다음</NextBtn>
                </form>
            </Card>
        </Wrap>
    );
}

/* ===== 스타일 ===== */

const Wrap = styled.div`
  min-height: 100dvh;
  display: grid;
  place-items: center;
  background: #fff;
`;

const Card = styled.div`
  width: min(520px, 92vw);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 20px 20px 24px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  font-size: 13px;
  cursor: pointer;

  .arrow {
    font-size: 14px;
  }

  .label {
    line-height: 1;
  }
`;

const Title = styled.h2`
  margin: 0 0 16px;
  font-size: 22px;
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 14px;
  label {
    font-size: 13px;
    color: #666;
  }
  input {
    height: 46px;
    border-radius: 12px;
    border: 1px solid #ddd;
    padding: 0 12px;
    font-size: 15px;
  }
`;

const PhoneHint = styled.p`
  margin: 2px 0 0;
  font-size: 11px;
  color: #6b7280;
`;

const SendRow = styled.div`
  margin: 8px 0 16px;
  button {
    width: 100%;
    height: 44px;
    border-radius: 12px;
    border: 1px solid #3479d6;
    color: #3479d6;
    background: #fff;
    font-weight: 800;
  }
`;

const ErrorMsg = styled.div`
  color: #c00;
  font-size: 13px;
  margin: 6px 0 4px;
`;

const NextBtn = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 12px;
  font-weight: 800;
  font-size: 16px;
  color: #fff;
  background: #3479d6;
  border: none;
`;
